"use client";

import { FileEditIcon, SquareTerminalIcon } from "lucide-react";

import type {
  ApplyPatchCallContentBlock,
  ApplyPatchCallOutputContentBlock,
} from "../../types";
import { cn, truncate } from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";

// ---------------------------------------------------------------------------
// ApplyPatchCallBlock
// ---------------------------------------------------------------------------

interface ApplyPatchCallBlockProps {
  block: ApplyPatchCallContentBlock;
  isStreaming?: boolean;
}

export const ApplyPatchCallBlock = ({
  block,
  isStreaming,
}: ApplyPatchCallBlockProps) => {
  const op = block.operation as { type?: string; path?: string; diff?: string };
  const label = [op.type, op.path].filter(Boolean).join(" ") || "Apply Patch";
  const code = op.diff || label;
  const lines = code.split("\n");
  const previewLines = lines.slice(0, 3).map((l) => truncate(l));
  const preview = previewLines.join(" | ") + (lines.length > 3 ? " ..." : "");

  return (
    <CollapsibleSection
      icon={<FileEditIcon className="h-3 w-3" />}
      label={label}
      preview={preview}
      copyContent={code}
      isStreaming={isStreaming}
    >
      <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 text-xs text-zinc-800">
        {code}
      </pre>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// ApplyPatchCallOutputBlock
// ---------------------------------------------------------------------------

interface ApplyPatchCallOutputBlockProps {
  block: ApplyPatchCallOutputContentBlock;
  isStreaming?: boolean;
}

export const ApplyPatchCallOutputBlock = ({
  block,
  isStreaming,
}: ApplyPatchCallOutputBlockProps) => {
  const output = block.output || "(no output)";
  const isSuccess = block.status === "completed";
  const preview = truncate(output);

  return (
    <CollapsibleSection
      icon={<SquareTerminalIcon className="h-3 w-3" />}
      label="Patch Output"
      preview={preview}
      copyContent={output}
      isStreaming={isStreaming}
    >
      <div className="flex flex-col gap-1.5">
        <span
          className={cn(
            "inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-medium",
            isSuccess ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600",
          )}
        >
          {block.status ?? "unknown"}
        </span>
        <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 font-mono text-xs text-zinc-800">
          {output}
        </pre>
      </div>
    </CollapsibleSection>
  );
};
